import { ethers } from "ethers";

export const SANCTIONS_COVERAGE_MESSAGE =
  "Chainalysis: no sanctions-list match. Connected-wallet risk and token blacklists are not assessed.";
export function usesSanctionsOracle(): boolean {
  return (
    process.env.REACT_APP_WALLET_SCREENING_PROVIDER === "chainalysis_oracle"
  );
}
export interface SanctionsScreening {
  provider: "chainalysis_oracle";
  coverage: "listed-address-sanctions-only";
  address: string;
  listed: boolean;
  blocked: boolean;
  reasons: string[];
  checkedAt: string;
  riskScore: null;
}
export async function screenSanctionsWallet(
  address: string,
  signal?: AbortSignal,
): Promise<SanctionsScreening> {
  const wallet = ethers.getAddress(address);
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (signal?.aborted) abort();
  signal?.addEventListener("abort", abort, { once: true });
  const timer = setTimeout(abort, 15000);
  try {
    const response = await fetch(
      `/api/screening?address=${encodeURIComponent(wallet)}`,
      {
        method: "GET",
        cache: "no-store",
        credentials: "same-origin",
        redirect: "error",
        signal: controller.signal,
      },
    );
    if (!response.ok)
      throw new Error(
        "Wallet sanctions screening is unavailable. Try again later.",
      );
    const text = await response.text();
    if (text.length > 16384)
      throw new Error("Invalid wallet screening response");
    let r: any;
    try {
      r = JSON.parse(text);
    } catch {
      throw new Error("Invalid wallet screening response");
    }
    const age = Date.now() - Date.parse(r?.checkedAt);
    if (
      r?.provider !== "chainalysis_oracle" ||
      r.coverage !== "listed-address-sanctions-only" ||
      r.address?.toLowerCase() !== wallet.toLowerCase() ||
      r.settlementChainId !== 84532 ||
      r.oracleChainId !== 8453 ||
      r.oracleAddress !== "0x3A91A31cB3dC49b4db9Ce721F50a9D076c8D739B" ||
      r.oracleCodeHash !==
        "0x7f071e09da8c4dd74a602df8a02b94c1f79b4e8a16c23b87d64326dcde3aa79a" ||
      typeof r.listed !== "boolean" ||
      r.blocked !== r.listed ||
      r.riskScore !== null ||
      r.connectedWalletAnalysis !== "not_assessed" ||
      r.tokenIssuerBlacklist !== "not_assessed" ||
      !Number.isFinite(age) ||
      age < -60000 ||
      age > 60000
    )
      throw new Error("Invalid or stale wallet screening response");
    return { ...r, reasons: r.listed ? ["Sanctions-list address match."] : [] };
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", abort);
  }
}
export async function assertFreshWalletScreening(
  address: string,
  chainId: number | bigint,
  signal?: AbortSignal,
): Promise<void> {
  if (!usesSanctionsOracle()) return;
  // The oracle screens EVM addresses on Base mainnet. Its list also applies
  // to Chikyu addresses; only these two testnet transaction flows are enabled.
  if (![84532n, 6281971n].includes(BigInt(chainId)))
    throw new Error(
      "This sanctions screening configuration is restricted to Base Sepolia and DogeOS Chikyu",
    );
  const result = await screenSanctionsWallet(address, signal);
  if (result.blocked) throw new Error("Wallet has a sanctions-list match");
}
